import React from "react";
import ReactDOM from "react-dom";
import { buildLabelsAndColors } from "./utils/mapPage/helpers";

export default function MapHoverPopup({
  hoverInfo,
  group,
  groups,
  themeColours,
  preferenceFilter,
  mapRef,
}) {
  if (!hoverInfo) return null;

  const hProps = hoverInfo.props || {};
  const areaName = hProps.area_name || String(hProps.area_id || "Unknown");

  // ── Parse stored bar items ────────────────────────────────────
  let items = hProps._allValuesSorted;
  if (typeof items === "string") { try { items = JSON.parse(items); } catch { items = null; } }
  if (!Array.isArray(items)) {
    let byKey = hProps._allValuesByKey;
    if (typeof byKey === "string") { try { byKey = JSON.parse(byKey); } catch { byKey = null; } }
    if (byKey && typeof byKey === "object") {
      items = Object.entries(byKey).map(([key, value]) => ({ key, label: key, value }));
    } else {
      let obj = hProps._allValues;
      if (typeof obj === "string") { try { obj = JSON.parse(obj); } catch { obj = null; } }
      items = obj && typeof obj === "object"
        ? Object.entries(obj).map(([key, value]) => ({ key, label: key, value }))
        : [];
    }
  }

  // ── Detect likert_grid drilled mode ─────────────────────────
  const [pfSvId] = String(preferenceFilter || "").split("|");
  const isDrilledGrid = group?.type === "likert_grid"
    && pfSvId && pfSvId !== "compare"
    && group?.subVariables?.includes(pfSvId);
  const drilledSubVar = isDrilledGrid ? (groups?.[pfSvId] || null) : null;
  const effectivePolarity = drilledSubVar?.polarity || group?.polarity || null;

  // ── Append computed net score if group/sub-var has polarity ──
  let byKeyForNet = hProps._allValuesByKey;
  if (typeof byKeyForNet === "string") { try { byKeyForNet = JSON.parse(byKeyForNet); } catch { byKeyForNet = null; } }
  if (byKeyForNet && typeof byKeyForNet === "object" && effectivePolarity) {
    const toArrNet = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
    const sumKeysNet = (keys) => keys.reduce((acc, k) => acc + (Number(byKeyForNet?.[k]) || 0), 0);
    const pos = sumKeysNet(toArrNet(effectivePolarity.pos));
    const neg = sumKeysNet(toArrNet(effectivePolarity.neg));
    const netVal = pos - neg;
    if (Number.isFinite(netVal)) {
      items = [...items, { key: "net1", label: "Net", value: netVal }];
    }
  }

  // ── Colors ───────────────────────────────────────────────────
  const { colors } = buildLabelsAndColors(groups || {}, themeColours || []);
  const fmtVal = (v) => `${Number(v).toFixed(1)}%`;
  const clampPct = (v) => Math.max(0, Math.min(100, v));

  // ── Helper: polarity-ordered items ───────────────────────────
  const orderByPolarity = (allItems, polarity, keys) => {
    const byKey = new Map((allItems || []).map((it) => [String(it?.key), it]));
    const ordered = [];
    const pushKeys = (vals) => {
      (Array.isArray(vals) ? vals : vals != null ? [vals] : []).forEach((k) => {
        const it = byKey.get(String(k));
        if (it && Number.isFinite(Number(it.value))) ordered.push(it);
      });
    };
    pushKeys(polarity?.pos);
    pushKeys(polarity?.neu);
    pushKeys(polarity?.neg);
    pushKeys(polarity?.dk);
    (keys || []).forEach((k) => {
      const it = byKey.get(String(k));
      if (it && Number.isFinite(Number(it.value)) && !ordered.some((x) => String(x.key) === String(k))) {
        ordered.push(it);
      }
    });
    return ordered;
  };

  // ── Visible bars ─────────────────────────────────────────────
  const baseItems = (() => {
    if (group?.type === "likert") {
      return orderByPolarity(items, group.polarity, group.keys);
    }
    if (group?.type === "likert_grid") {
      // Drilled mode: treat like a regular likert using the sub-variable's polarity
      if (isDrilledGrid && drilledSubVar?.polarity) {
        return orderByPolarity(items, drilledSubVar.polarity, drilledSubVar.keys);
      }
      // Compare mode: net scores per sub-variable (can be negative), already sorted
      return (items || []).filter((it) => Number.isFinite(Number(it?.value)));
    }
    return (items || []).filter((it) => Number(it?.value) > 0).slice(0, 10);
  })();

  const visibleBars = baseItems.filter(({ value }) => Number.isFinite(Number(value)));

  const hiddenNetValue = (() => {
    const netItem = (items || []).find((it) => it?.key === "net1");
    const v = Number(netItem?.value);
    return Number.isFinite(v) ? v : null;
  })();

  // ── Popup positioning ─────────────────────────────────────────
  const mapCanvas = mapRef?.current?.getMap()?.getCanvas();
  const mapRect = mapCanvas?.getBoundingClientRect() ?? { left: 0, top: 0 };
  const vx = (hoverInfo.x ?? 0) + mapRect.left;
  const vy = (hoverInfo.y ?? 0) + mapRect.top;
  const PW = 280, PH = 500, PAD = 20;
  const goRight = (window.innerWidth - vx) >= PW + PAD;
  const goLeft = vx >= PW + PAD;
  const hDir = goRight ? "right" : goLeft ? "left" : (window.innerWidth - vx) >= vx ? "right" : "left";
  const vDir = (window.innerHeight - vy) >= PH + PAD ? "below" : vy >= PH + PAD ? "above" : "below";
  const posStyle = {
    position: "fixed",
    zIndex: 99999,
    pointerEvents: "none",
    ...(vDir === "below" ? { top: vy + 10 } : { bottom: window.innerHeight - vy + 10 }),
    ...(hDir === "right" ? { left: vx + 10 } : { right: window.innerWidth - vx + 10 }),
  };

  const renderNetBar = (displayNum, fillOverride) => {
    const barW = clampPct(Math.abs(displayNum));
    const fill = fillOverride || (displayNum >= 0 ? "#16a34a" : "#dc2626");
    return (
      <div style={{ height: 3, background: "#f0f2f7", borderRadius: 3, position: "relative", overflow: "hidden" }}>
        <div style={{
          position: "absolute", top: 0, height: "100%",
          left: displayNum >= 0 ? "50%" : `${50 - barW / 2}%`,
          width: `${barW / 2}%`,
          background: fill,
          borderRadius: 3,
        }} />
      </div>
    );
  };

  const hasNetInBars = visibleBars.some((b) => b.key === "net1");

  return ReactDOM.createPortal(
    <div style={posStyle}>
      <div style={{
        width: 260,
        fontFamily: "'DM Sans', system-ui, sans-serif",
        background: "#ffffff",
        borderRadius: 10,
        boxShadow: "0 4px 20px rgba(0,0,0,0.13), 0 1px 4px rgba(0,0,0,0.08)",
        overflow: "hidden",
      }}>

        {/* Header */}
        <div style={{ padding: "10px 13px 8px", borderBottom: "1px solid #f0f2f7" }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#1a1d23", lineHeight: 1.3 }}>
            {areaName}
          </div>
        </div>

        {/* Bar chart */}
        <div style={{ padding: "9px 13px", borderBottom: (!hasNetInBars && Number.isFinite(hiddenNetValue)) ? "1px solid #f0f2f7" : "none" }}>
          {visibleBars.length === 0 ? (
            <div style={{ fontSize: 11, color: "#9ca3af" }}>No data</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {visibleBars.map(({ key, label, value }) => {
                const num = Number(value);
                const isNet = key === "net1";
                const toArrPol = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
                const isPolPos = !isNet && effectivePolarity && toArrPol(effectivePolarity.pos).includes(key);
                const isPolNeg = !isNet && effectivePolarity && toArrPol(effectivePolarity.neg).includes(key);
                const isLikertGridSector = group?.type === "likert_grid" && group?.subVariables?.includes(key);
                const barColor = isNet
                  ? (num >= 0 ? "#16a34a" : "#dc2626")
                  : isPolPos ? "#16a34a"
                  : isPolNeg ? "#dc2626"
                  : group?.subColors?.[key] || colors[key] || "#2563eb";
                const rowBg = isPolPos
                  ? "rgba(22,163,74,0.05)"
                  : isPolNeg ? "rgba(220,38,38,0.05)"
                  : "transparent";
                const rowBorder = isPolPos
                  ? "rgba(22,163,74,0.2)"
                  : isPolNeg ? "rgba(220,38,38,0.2)"
                  : "transparent";
                const valColor = isNet
                  ? (num >= 0 ? "#16a34a" : "#dc2626")
                  : isPolPos ? "#16a34a"
                  : isPolNeg ? "#dc2626"
                  : "#1a1d23";

                return (
                  <div key={key} style={{ padding: "4px 6px", borderRadius: 6, background: rowBg, border: `1px solid ${rowBorder}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 2, gap: 8 }}>
                      <span style={{ flex: 1, fontSize: 11, color: "#4b5563", fontWeight: 600, lineHeight: 1.25, whiteSpace: "normal" }}>
                        {label}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: valColor, flexShrink: 0 }}>
                        {fmtVal(num)}
                      </span>
                    </div>
                    {isNet || isLikertGridSector ? renderNetBar(num, isLikertGridSector ? barColor : undefined) : (
                      <div style={{ height: 3, background: "#f0f2f7", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{
                          height: "100%", width: `${clampPct(num)}%`,
                          background: barColor, borderRadius: 3,
                          transition: "width 0.15s ease-out",
                        }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Net score footer when not already shown as a bar */}
        {!hasNetInBars && Number.isFinite(hiddenNetValue) && (
          <div style={{ padding: "6px 13px 8px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 2 }}>
              <span style={{ fontSize: 11, color: "#4b5563", fontWeight: 600 }}>Net</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: hiddenNetValue >= 0 ? "#16a34a" : "#dc2626" }}>
                {fmtVal(hiddenNetValue)}
              </span>
            </div>
            {renderNetBar(hiddenNetValue)}
          </div>
        )}

      </div>
    </div>,
    document.body
  );
}
